import { ReactElement, useState } from 'react';
import { ArrowRight, CaretDown, CaretUp, Clock } from '@phosphor-icons/react';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import { useRouter } from 'next/router';
import { isSubscriptionProduct } from '@helpers/helper';
import * as ApiModels from '@typings/api-models';
import { Asset, Media, ProductType } from '@typings/graphql-models';
import ProductTypeContainer from '@lib/UserProfile/components/ProductTypeContainer';
import DOMPurify from 'isomorphic-dompurify';
import HandbookPdfContainer from '@lib/UserProfile/components/HandbookPdf/HandbookPdfContainer';
import NewServiceCard from '../NewServiceCard';
import Button from '../DesignLibrary/Button';
import s from './ConsultationCard.module.scss';
import ProgressIndicator from '../ProgressIndicator';

type Comments = Array<
  Pick<ApiModels.Message, 'id' | 'title' | 'text' | 'isDeleted' | 'createdAt' | 'type'> & {
    parent?:
      | (Pick<ApiModels.Message, 'id'> & {
          owner?: {
            name?: string | null;
            image?: { url: string } | null;
          } | null;
        })
      | null;
    owner?: {
      name?: string | null;
      image?: { url: string } | null;
    } | null;
    tags: {
      id: string;
      name: string;
    }[];
    medias: Pick<Media, 'id' | 's3OriginId' | 'vimeoThumbnailUrl' | 'vimeoUri' | 'type'>[];
    children: Array<{
      owner?: {
        id: string;
        name?: string | null;
        image?: { url: string } | null;
      } | null;
    }>;
  }
>;

type ThisProduct = Pick<ApiModels.Product, 'id' | 'slug' | 'title' | 'includesBroadcasts' | 'isActive'> & {
  comments?: Comments;
  image?: Pick<Asset, 'url'> | null;
  prices: Array<Pick<ApiModels.Price, 'id' | 'price' | 'isActive' | 'sessionDuration'>>;
  productTypes: Pick<ProductType, 'title' | 'noodleProductType' | 'slug'>[];
  initiateWorkflowCTAs: Array<Pick<ApiModels.InitiateWorkflowCTA, 'id' | 'title' | 'CTA' | 'description'>>;
  messageFromCreator?: { html: string } | null;
} & Parameters<typeof isSubscriptionProduct>[0] &
  Parameters<typeof NewServiceCard>[0]['product'] &
  Parameters<typeof ProductTypeContainer>[0]['bytes'][number] &
  Parameters<typeof HandbookPdfContainer>[0]['bytes'][number];

type ConsultationCardProps = {
  product?: ThisProduct;
  creatorSlug: string;
  open?: boolean;
};

const ConsultationCard = ({ product, creatorSlug, open }: ConsultationCardProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(open);
  const [isRouting, setIsRouting] = useState(false);

  const router = useRouter();

  const getPrice = (prices: unknown): string => {
    const validPrices = Array.isArray(prices) ? prices : [];

    if (!validPrices || validPrices.length === 0) return 'free';

    const validPrice = validPrices.find(p => p.price > 0);

    if (!validPrice) return 'free';

    return `${validPrice.price.toFixed(2)} ${validPrice.currency}`;
  };

  const sessionDuration = product?.prices?.[0]?.sessionDuration;

  const getTextFromHTML = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const productDescription = getTextFromHTML(product?.messageFromCreator?.html ?? '');

  const routeToProduct = async (): Promise<void> => {
    if (!product) return;
    setIsRouting(true);
    await router.push({
      pathname: getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug: product.slug }),
      query: { fromTab: 'Bookings' },
    });
    setIsRouting(false);
  };

  const showDescription = product?.messageFromCreator?.html && getTextFromHTML(product.messageFromCreator.html).trim();

  return (
    <div className={s.consultationCard}>
      {showDescription && (
        <Button variant="primary" size="md" className={s.consultationCard__caret} onClick={() => setIsOpen(!isOpen)} aria-label="Toggle description">
          {isOpen ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </Button>
      )}

      <div className={s.consultationCard__mainSection}>
        <div className={s.consultationCard__title} onClick={() => showDescription && setIsOpen(!isOpen)} style={showDescription ? { cursor: 'pointer' } : {}}>
          {product?.title}
        </div>

        {isOpen && product?.messageFromCreator?.html && (
          <div
            className={s.consultationCard__description}
            style={productDescription.length < 400 ? { columns: 1 } : {}}
          >
            {productDescription}
          </div>
        )}

        <div className={s.consultationCard__mainSection__footer}>
          <Button variant="primary" size="md" onClick={routeToProduct} className={s.consultationCard__mainSection__footer__button}>
            Book for {getPrice(product?.prices)}
            {isRouting ? <ProgressIndicator color="var(--color-primary)" size={16} /> : <ArrowRight size={16} />}
          </Button>

          {sessionDuration && (
            <div className={s.consultationCard__mainSection__footer__tag}>
              <Clock size={16} />
              <span>{sessionDuration} minutes</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationCard;
